import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { CheckoutFacade } from '@checkout/data-access';
import {
  CmsMarketingPreferencesPage,
  MarketingPreferencesForm,
  MarketingPreferencesFormConfig,
  MarketingPreferencesFormField,
  MarketingPreferencesFormValue,
} from '@checkout/util-models';
import { BasketFacade, QuoteDetails } from '@domgen/dgx-fe-basket';
import { CmsContainer } from '@domgen/dgx-fe-cms';
import { CustomerDetails, filterNullUndefined } from '@domgen/dgx-fe-common';
import { controlValue } from '@domgen/dgx-fe-forms/shared';
import { ComponentStore } from '@ngrx/component-store';
import { Observable, combineLatest, first, map, mergeMap, tap } from 'rxjs';
import { formatCustomerDetailsToMarketingPreferencesFormValue } from '../../utils/marketing-preferences-form/marketing-preferences-form.utils';
import { MarketingPreferencesFormBuilder } from '../marketing-preferences-form-builder/marketing-preferences-form-builder.service';

export interface ViewModel {
  cmsData: CmsMarketingPreferencesPage;
  form?: FormGroup<MarketingPreferencesForm>;
  selectedQuotes?: QuoteDetails[];
  marketingPreferencesValue: string;
}

export interface ComponentState {
  form?: FormGroup<MarketingPreferencesForm>;
}

@Injectable()
export class MarketingPreferencesFormComponentService extends ComponentStore<ComponentState> {
  private readonly formInitialValue$: Observable<
    MarketingPreferencesFormValue | undefined
  > = this.checkoutFacade.customerDetails$.pipe(
    map((customerDetails: CustomerDetails | undefined) =>
      formatCustomerDetailsToMarketingPreferencesFormValue(customerDetails)
    )
  );

  readonly form$: Observable<FormGroup<MarketingPreferencesForm>> = this.select(
    (state: ComponentState) => state.form
  ).pipe(filterNullUndefined());

  private readonly formConfig$: Observable<MarketingPreferencesFormConfig> =
    combineLatest({
      cmsData: this.cmsContainer.content$,
      initialValue: this.formInitialValue$,
    });

  private readonly marketingPreferencesValue$: Observable<string> =
    combineLatest([
      this.form$.pipe(
        controlValue<boolean>(
          MarketingPreferencesFormField.MarketingPreferenceEmail
        )
      ),
      this.form$.pipe(
        controlValue<boolean>(
          MarketingPreferencesFormField.MarketingPreferencePost
        )
      ),
      this.form$.pipe(
        controlValue<boolean>(
          MarketingPreferencesFormField.MarketingPreferenceTelephone
        )
      ),
    ]).pipe(
      map(
        ([
          marketingPreferenceEmail,
          marketingPreferencePost,
          marketingPreferenceTelephone,
        ]) => {
          const marketingPreferenceList = [
            marketingPreferenceTelephone ? 'phone' : null,
            marketingPreferencePost ? 'post' : null,
            marketingPreferenceEmail ? 'email' : null,
          ].filter(Boolean);

          return marketingPreferenceList.length
            ? marketingPreferenceList.join('|')
            : 'none selected';
        }
      )
    );

  readonly vm$: Observable<ViewModel> = combineLatest({
    cmsData: this.cmsContainer.content$,
    form: this.form$,
    selectedQuotes: this.basketFacade.selectedQuotes$,
    marketingPreferencesValue: this.marketingPreferencesValue$,
  });

  constructor(
    private readonly basketFacade: BasketFacade,
    private readonly checkoutFacade: CheckoutFacade,
    private readonly cmsContainer: CmsContainer<CmsMarketingPreferencesPage>,
    private readonly marketingPreferencesFormBuilder: MarketingPreferencesFormBuilder
  ) {
    super({});
  }

  private readonly updateForm = this.updater(
    (state: ComponentState, form: FormGroup<MarketingPreferencesForm>) => ({
      ...state,
      form,
    })
  );

  readonly buildForm = this.effect((trigger$: Observable<void>) =>
    trigger$.pipe(
      mergeMap(() => this.formConfig$),
      first(),
      map((config: MarketingPreferencesFormConfig) =>
        this.marketingPreferencesFormBuilder.build(config)
      ),
      tap((form) => this.updateForm(form))
    )
  );
}
